import { WorkareaSelectedPricing } from '../../domain/workareas/workareaPricingApi'
import { ItemClasses, PricingType, PricingTypes } from "../../common/constants";
import { CompletedWorkareaVisit } from "../../domain/invoicing/invoicingApi";
import { ActualItem, EstimatedItem, WorkareaItemBase, isActualItem, isEstimatedItem } from "../../domain/workareaItem";
import { sum } from "../../helpers/array";
import { roundToPrecision } from "../../helpers/math";
import { hasValue } from "../../helpers/nullable";
import { ChangeSet, ItemVisitData, ItemWorkareaData, calculateTotalStandardPrice, getUpdatedItem } from "./itemModel";

type NotInvoicedWorkareaPrice = {
  workareaId: number
  price: number
}

export type VisitSummaryItem = {
  visitId: string
  billed: boolean
  totalPrice: number
  hours: number
  unsure: boolean
  notInvoicedWorkareaPrices: NotInvoicedWorkareaPrice[]
  hasInvoicedItem: boolean
}

const isForVisit = (item: WorkareaItemBase, visitId: string) => {
  const actual = item as ActualItem
  return actual.visitId === visitId
}

const getVisitItems = (visitId: string, actualServerItems: ActualItem[], changeSet: ChangeSet): ActualItem[] => {
  return changeSet.addedItems.filter(x => isForVisit(x, visitId))
    .concat(actualServerItems.filter(x => x.visitId === visitId))
    .map(x => getUpdatedItem(x, changeSet))
    .filter(x => !x.isDeleted)
    .map(x => x.item) as ActualItem[]
}

export type VisitPricing = { visitId: string, all: WorkareaSelectedPricing[], uninvoiced: WorkareaSelectedPricing[] }

export const buildVisitSummary = (visits: ItemVisitData[], actualServerItems: ActualItem[], changeSet: ChangeSet, pricing: VisitPricing[] = []): VisitSummaryItem[] => {
  return visits.map(visit => {
    const visitItems = getVisitItems(visit.id, actualServerItems, changeSet)
    const savedPricing = pricing.find(x => x.visitId === visit.id)
    const laborItems = visitItems.filter(x => x.class === ItemClasses.Labor)
    const workareaIds = Array.from(new Set(visitItems.map(x => x.workareaId)))
    return {
      visitId: visit.id,
      billed: visit.billed,
      totalPrice: savedPricing ? sum(savedPricing.all, x => (x.completedVisits ?? x.actual).customerTotal) : sum(visitItems, x => x.totalPrice),
      hours: roundToPrecision(sum(laborItems, x => x.quantity), 2),
      unsure: visitItems.some(x => isActualItem(x) && x.isBillableUnknown),
      notInvoicedWorkareaPrices: savedPricing ? savedPricing.uninvoiced.map(x => ({ workareaId: x.workareaId, price: (x.completedVisits ?? x.actual).customerTotal })) : workareaIds.map(workareaId => ({
        workareaId,
        price: sum(visitItems.filter(x => x.workareaId === workareaId && !hasValue(x.invoiceId)), x => x.totalPrice)
      })),
      hasInvoicedItem: visitItems.some(x => hasValue(x.invoiceId))
    }
  })
}

export type WorkareaSummary = {
  workareaId: number
  isOnHold: boolean
  estimatedHours: number
  estimatedHoursPerOccurrence: number,
  estimatedPrice: number
  estimatedPricePerOccurrence: number
  estimatedCostPlusTax: number
  estimatedOverhead: number
  estimatedRecovery: number
  estimatedGrossProfit: number
  estimatedNetProfit: number
  actualHours: number
  standardPrice: number
  finalPrice: number
  actualCostPlusTax: number
  actualOverhead: number
  actualRecovery: number
  actualGrossProfit: number
  actualNetProfit: number
  nonVisitNotInvoicedPrice: number
  hasActualItems: boolean
  estimatedBreakEven: number
  estimatedNetProfitPercentage: number
  finalNetProfitPercentage: number
  perVisitNetProfitPercentage: number
  perVisitPrice: number
  isOverridden: boolean,
  total: number,
  isEstimated: boolean
  isPerVisit: boolean
  pricingType: PricingType
  recovery: number
}

export const newWorkareaSummary = (workareaId: number): WorkareaSummary => ({
  workareaId,
  isOnHold: false,
  estimatedHours: 0,
  estimatedHoursPerOccurrence: 0,
  estimatedPrice: 0,
  estimatedPricePerOccurrence: 0,
  estimatedCostPlusTax: 0,
  estimatedOverhead: 0,
  estimatedRecovery: 0,
  estimatedGrossProfit: 0,
  estimatedNetProfit: 0,
  estimatedBreakEven: 0,
  actualHours: 0,
  standardPrice: 0,
  finalPrice: 0,
  actualCostPlusTax: 0,
  actualOverhead: 0,
  actualRecovery: 0,
  actualGrossProfit: 0,
  actualNetProfit: 0,
  nonVisitNotInvoicedPrice: 0,
  hasActualItems: false,
  estimatedNetProfitPercentage: 0,
  finalNetProfitPercentage: 0,
  perVisitNetProfitPercentage: 0,
  perVisitPrice: 0,
  isOverridden: false,
  total: 0,
  isEstimated: false,
  isPerVisit: false,
  pricingType: PricingTypes.FlatRate,
  recovery: 0,
})

export const buildWorkareaSummaries = (
  workareas: ItemWorkareaData[],
  estimatedServerItems: EstimatedItem[],
  actualServerItems: ActualItem[],
  changeSet: ChangeSet,
  completedWorkareaVisits: CompletedWorkareaVisit[],
  nonVisitPricing: WorkareaSelectedPricing[] = []
): WorkareaSummary[] => {
  const allItems = changeSet.addedItems
    .concat(estimatedServerItems)
    .concat(actualServerItems)
    .map(x => getUpdatedItem(x, changeSet))
    .filter(x => !x.isDeleted)
    .map(x => x.item)
  const data: { [index: number]: WorkareaSummary } = {}

  allItems.forEach(item => {
    if (!data[item.workareaId]) {
      data[item.workareaId] = newWorkareaSummary(item.workareaId)
    }
    const summary = data[item.workareaId]

    const totalOverhead = Math.round(item.overhead * item.quantity)
    const totalCostPlusTax = item.totalBreakeven - totalOverhead

    if (isEstimatedItem(item)) {
      summary.estimatedPrice += item.totalPrice
      summary.estimatedCostPlusTax += totalCostPlusTax
      summary.estimatedOverhead += totalOverhead
      summary.estimatedBreakEven += item.totalBreakeven
      if (item.class === ItemClasses.Labor) {
        summary.estimatedHours += item.quantity
      }
    } else if (isActualItem(item)) {
      summary.hasActualItems = true
      summary.finalPrice += item.totalPrice
      summary.standardPrice += calculateTotalStandardPrice(item)
      summary.actualCostPlusTax += totalCostPlusTax
      summary.actualOverhead += totalOverhead
      if (item.class === ItemClasses.Labor) {
        summary.actualHours += item.quantity
      }
      if (!hasValue(item.visitId) && !hasValue(item.invoiceId)) {
        summary.nonVisitNotInvoicedPrice += item.totalPrice
      }
    }
  })

  return workareas.map(x => {
    let summary = data[x.id] ?? newWorkareaSummary(x.id)
    const estimatedOccurrences = x.pricingType === PricingTypes.PerVisit
      ? 1
      : x.estimatedOccurrences ?? 1
    summary = {
      ...summary,
      estimatedHoursPerOccurrence: summary.estimatedHours,
      estimatedHours: summary.estimatedHours * estimatedOccurrences,
      estimatedPricePerOccurrence: summary.estimatedPrice,
      estimatedPrice: summary.estimatedPrice * estimatedOccurrences,
      estimatedCostPlusTax: summary.estimatedCostPlusTax * estimatedOccurrences,
      estimatedOverhead: summary.estimatedOverhead * estimatedOccurrences,
      estimatedRecovery: summary.estimatedRecovery * estimatedOccurrences,
    }
    
    const pricing = x.pricing
    if (pricing) {
      summary.estimatedPrice = pricing.calculatedEstimate.customerTotal
      summary.estimatedPricePerOccurrence = estimatedOccurrences > 0 ? pricing.calculatedEstimate.customerTotal / estimatedOccurrences : 0
      summary.estimatedRecovery = pricing.calculatedEstimate.recovery
      summary.finalPrice = pricing.actual.customerTotal
      summary.actualRecovery = pricing.actual.recovery
      summary.standardPrice = pricing.actual.customerTotal
    }
    const nonVisit = nonVisitPricing.find(p => p.workareaId === x.id)
    if (nonVisit) {
      summary.nonVisitNotInvoicedPrice = nonVisit.actual.customerTotal
    }
    const finalPrice = determineFinalPrice(x, summary, completedWorkareaVisits)
    const perVisitPrice = hasValue(x.quotedPrice) ? x.quotedPrice : summary.estimatedPrice
    const perVisitTotalPrice = perVisitPrice * estimatedOccurrences
    const isPerVisit = x.pricingType === PricingTypes.PerVisit
    const isEstimated = x.pricingType === PricingTypes.TimeAndMaterials || x.pricingType === PricingTypes.FlatRate || isPerVisit
    const total = (isPerVisit ? perVisitTotalPrice : isEstimated ? summary.estimatedPrice : finalPrice)
    const isOverridden = [PricingTypes.PerVisit, PricingTypes.QuotedPrice].includes(x.pricingType)
      ? (total && total !== summary.estimatedPrice || hasValue(x.customQuotedPrice))
      : false

    const recovery = (x.pricingType === PricingTypes.QuotedPrice || isPerVisit) && hasValue(x.quotedPrice)
      ? x.recovery ?? 0
      : summary.estimatedRecovery
    const estimatedWorkPrice = (isOverridden ? total : summary.estimatedPrice) - (isOverridden ? recovery : summary.estimatedRecovery)
    const estimatedGrossProfit = estimatedWorkPrice - summary.estimatedCostPlusTax
    const estimatedNetProfit = estimatedGrossProfit - summary.estimatedOverhead
    const profitPercentage = (price: number, allowance: number) => {
      const workPrice = price - allowance
      return workPrice === 0 ? 0 : Number((estimatedNetProfit / workPrice).toFixed(4))
    }
    const estimatedNetProfitPercentage = profitPercentage(summary.estimatedPrice, summary.estimatedRecovery)
    const finalRecovery = isPerVisit
      ? sum(completedWorkareaVisits.filter(v => v.workareaId === x.id), v => v.visitRecovery ?? 0)
      : x.pricingType === PricingTypes.QuotedPrice && hasValue(x.quotedPrice) ? x.recovery ?? 0 : summary.actualRecovery
    const finalNetProfitPercentage = profitPercentage(finalPrice, finalRecovery)
    const perVisitNetProfitPercentage = profitPercentage(perVisitPrice, recovery)
    const actualGrossProfit = finalPrice - finalRecovery - summary.actualCostPlusTax
    const actualNetProfit = actualGrossProfit - summary.actualOverhead
    const isOnHold = x.isOnHold

    return { 
      ...summary, 
      isOverridden,
      recovery,
      actualRecovery: finalRecovery,
      total,
      isEstimated,
      isPerVisit,
      isOnHold, 
      finalPrice, 
      estimatedGrossProfit, 
      estimatedNetProfit, 
      actualGrossProfit, 
      actualNetProfit, 
      estimatedNetProfitPercentage, 
      finalNetProfitPercentage, 
      perVisitNetProfitPercentage,
      perVisitPrice,
      perVisitTotalPrice,
      pricingType: x.pricingType,
    }
  })
}

const determineFinalPrice = (workarea:ItemWorkareaData, summary: WorkareaSummary, completedWorkareaVisits: CompletedWorkareaVisit[]): number => {
  if (workarea.pricingType === PricingTypes.QuotedPrice) {
    return hasValue(workarea.quotedPrice) ? workarea.quotedPrice : summary.estimatedPrice
  }

  if (workarea.pricingType === PricingTypes.PerVisit) {
    return completedWorkareaVisits.filter(x => x.workareaId === workarea.id)
      .reduce((total, x) => total + (x.visitPrice ?? 0), 0)
  }

  return summary.finalPrice
}